import {
  FormKind,
  RecreationEditForm,
  RecreationFormValues,
} from '@/components/shared/form';
import { Error, LoadingContainer } from '@/components/shared/parts';
import { Recreation } from '@/types';
import {
  FILE_READ_FAILED_MESSAGE,
  IMAGE_DELETE_FAILED_MESSAGE,
  IMAGE_UPLOAD_FAILED_MESSAGE,
  MAX_IMAGE_FILE_SIZE_MESSAGE,
  fileApiErrorMessages,
  getQueryStringValueByKey,
  isEmpty,
  isFileSizeExceeded,
  mountComponent,
} from '@/utils';
import axios, { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { useRecreations, useRecreationImage } from '../../hooks';

const RECREATION_FETCH_FAILED_MESSAGE =
  'レク情報の取得に失敗しました。時間をおいて再度お試しください。';

export type UseFile = {
  handleFileAdd: (files: FileList | null, kind: string) => void;
  handleFileDelete: (id: number) => Promise<void>;
  isLoading: boolean;
};

const RecreationEdit: React.FC = () => {
  const [errors, setErrors] = useState<Array<string>>([]);
  const [recreation, setRecreation] = useState<Recreation>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);
  const id = window.location.pathname.split('/')[3];
  const formKind = getQueryStringValueByKey('formKind') as FormKind;
  const { fetchRecreation, updateRecreation } = useRecreations();
  const { createRecreationImage, deleteRecreationImage } = useRecreationImage();

  useEffect(() => {
    (async () => {
      if (id === undefined) return;
      try {
        const recreationResponse = await fetchRecreation(id);
        setRecreation({ ...recreationResponse });
      } catch (e) {
        setErrors(fileApiErrorMessages(e, RECREATION_FETCH_FAILED_MESSAGE));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [fetchRecreation, id]);

  const handleFileAdd = (files: FileList | null, kind = 'slider') => {
    if (!files || files.length <= 0) return;
    if (!recreation) return;
    // 送信中の二重実行を防ぐ（送信後に input をクリアしており同じファイルを再選択できるため）
    if (isFileLoading) return;
    const file = files[0];
    // 大きすぎるファイルはアップロードに時間がかかりサーバー側でタイムアウト（503）するため、
    // 送信前に画面表記と同じ上限で弾く
    if (isFileSizeExceeded(file)) {
      setErrors([MAX_IMAGE_FILE_SIZE_MESSAGE]);
      return;
    }
    setErrors([]);
    // 読み込み自体に時間がかかるため、ファイル読み込みの開始時点でローディングを立てる
    setIsFileLoading(true);
    const reader = new FileReader();
    reader.onerror = () => {
      setErrors([FILE_READ_FAILED_MESSAGE]);
      setIsFileLoading(false);
    };
    reader.onload = async (event) => {
      try {
        const result = event.target?.result;
        if (typeof result !== 'string' || result.length === 0) {
          setErrors([FILE_READ_FAILED_MESSAGE]);
          return;
        }
        const requestBody: { [key: string]: Record<string, unknown> } = {
          recreationImage: {
            image: result,
            filename: file.name,
            kind: kind,
          },
        };
        const createdImage = await createRecreationImage(
          recreation.id,
          requestBody
        );
        setRecreation((prev) =>
          prev ? { ...prev, images: [...prev.images, createdImage] } : prev
        );
      } catch (e) {
        setErrors(fileApiErrorMessages(e, IMAGE_UPLOAD_FAILED_MESSAGE));
      } finally {
        setIsFileLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileDelete = async (id: number): Promise<void> => {
    if (!recreation) return;
    setErrors([]);
    try {
      await deleteRecreationImage(recreation.id, id);
      setRecreation((prev) =>
        prev
          ? { ...prev, images: prev.images.filter((image) => image.id !== id) }
          : prev
      );
    } catch (e) {
      setErrors(fileApiErrorMessages(e, IMAGE_DELETE_FAILED_MESSAGE));
    }
  };

  const onSubmit = async (values: RecreationFormValues): Promise<void> => {
    setErrors([]);
    if (!recreation) return;

    const selectedPrefectures = values.prefectures ?? [];

    const requestBody: { [key: string]: Record<string, unknown> } = {
      recreation: {
        title: values.title || recreation.title,
        secondTitle: values.secondTitle || recreation.secondTitle,
        minutes: values.minutes || recreation.minutes,
        description: values.description || recreation.description,
        amount: values.amount || recreation.amount,
        kind: values.kind || recreation.kind,
        flowOfDay: values.flowOfDay || recreation.flowOfDay,
        capacity: values.capacity || recreation.capacity,
        isWithholdingTax: values.isWithholdingTax,
        materialAmount: values.materialAmount || recreation.materialAmount,
        extraInformation:
          values.extraInformation || recreation.extraInformation,
        youtubeId: values.youtubeId || recreation.youtubeId,
        borrowItem: values.borrowItem || recreation.borrowItem,
        bringYourOwnItem:
          values.bringYourOwnItem || recreation.bringYourOwnItem,
        additionalFacilityFee:
          values.additionalFacilityFee || recreation.additionalFacilityFee,
        imageUrl: values.imageUrl,
        category: values.category || recreation.category.key,
        userId: recreation.userId,
        status: 'in_progress',
        recreationProfileAttributes: { profileId: values.profileId },
        recreationPrefecturesAttributes: selectedPrefectures.map((p) => ({
          name: p,
        })),
      },
    };

    console.log(requestBody);

    try {
      await updateRecreation(id, requestBody);
      const noticeText = 'レクを更新しました！';
      window.location.href = `/partners/recreations/${id}?notice=${noticeText}`;
      // TODO(okubo): redirectによる画面遷移
      //
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const axiosError = e as AxiosError<Array<string>>;
        if (axiosError.response) {
          setErrors(axiosError.response.data);
          console.log(axiosError.response.data);
        }
      }
    }
  };

  if (isLoading) {
    return <LoadingContainer />;
  }
  if (!recreation) {
    return <></>;
  }
  return (
    <div>
      {!isEmpty(errors) && <Error errors={errors} />}
      <RecreationEditForm
        kind={formKind}
        recreation={recreation}
        setRecreation={setRecreation}
        onSubmit={onSubmit}
        useFile={{ handleFileAdd, handleFileDelete, isLoading: isFileLoading }}
      />
    </div>
  );
};

mountComponent('#recreationEdit', () => <RecreationEdit />);
